import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { MAX_FILE_MB, type FileInfo, type FolderInfo, type MeResponse, type ProjectDetail, type ProjectSummary } from "@handvero/shared";
import { api, ApiFailure, errorText, upload } from "../api";
import { prepareUpload } from "./images";
import { deepCount, FILE_ACCEPT, FolderNameSheet, folderPath, NewProject, sizeText, Viewer } from "./MobileApp";
import { CalendarView, NotesView } from "./NotesCalendar";
import { ChatView, TodayPanel } from "./Chat";
import { PhoneLinkPanel } from "./PhoneLink";
import { SettingsProvider, SettingsSheet, useSettings } from "./settings";
import { EmptyState, Icon, LOGO, type IconName, Message, Sheet, splitNumber, useUnauthorizedRedirect } from "./ui";
import { SupportBanner } from "../components/SupportBanner";
import { AdminPage } from "./Admin";
import { DevicesView } from "./Geraete";
import { HandoverSheet } from "./Handover";
import { GlobalHits } from "./GlobalHits";
import { BillingForm, CustomersView } from "./Customers";
import { DOC_TABS, DocumentsView, ProjectDocuments } from "./Documents";
import { BankView } from "./Bank";
import { DashboardView, ExpensesView } from "./Bookkeeping";
import { AccountView } from "./Account";
import { Mascot } from "./Mascot";
import { QueueBanner } from "./Teilen";
import { GuideHost } from "./Guide";
import { ProjectAccessSheet } from "./ProjectAccess";
import { ProtocolsView } from "./Protocols";
import { PlansView } from "./Plans";
import { TrainingsPage } from "./Trainings";
import { PcSyncBadge } from "./PcFolder";
import { startPcSync } from "./pcSync";
import "./app.css";
import "./desktop.css";

/*
 * HandVero für den PC: dieselben Daten wie die Handy-App, aufgeteilt auf große Flächen.
 * Links die Navigation, daneben Projekte, Ordnerbaum und Dateien. Dateien zieht man aus dem
 * Explorer direkt in einen Ordner, Fotos vom Handy erscheinen hier ohne Zutun.
 */

const TILE_WIDTH: Record<2 | 3 | 4, number> = { 2: 300, 3: 210, 4: 150 };
const HV_FILE = "application/x-handvero-file";

/* ---------- Kopf ---------- */

export function DHead(props: { kicker: string; title: string; num?: string | null; sub?: string; actions?: ReactNode; children?: ReactNode }) {
  return (
    <header className="dhead">
      <div className="dhead-row">
        <div className="dhead-text">
          <p className="dkicker">▼ {props.kicker}</p>
          <h1 className="dtitle">
            {props.num && <span className="hero-num">{props.num}</span>}
            <span>{props.title}</span>
          </h1>
          {props.sub && <p className="dsub">{props.sub}</p>}
        </div>
        {props.actions && <div className="dhead-actions">{props.actions}</div>}
      </div>
      {props.children}
    </header>
  );
}

/* ---------- Hochladen mit Warteschlange ---------- */

interface QueueItem {
  key: number;
  name: string;
  progress: number;
  status: "prepare" | "upload" | "done" | "error";
  error?: string;
}

function useUploads(onSaved: (file: FileInfo, folderId: string) => void, onAuth: (err: unknown) => boolean) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const keyRef = useRef(0);

  const uploadTo = useCallback(
    async (folderId: string, list: File[]) => {
      if (!list.length) return;
      const items = list.map((f) => ({ key: ++keyRef.current, name: f.name, progress: 0, status: "prepare" as const }));
      setQueue((q) => [...q, ...items]);
      const update = (key: number, patch: Partial<QueueItem>) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...patch } : x)));
      for (const [i, raw] of list.entries()) {
        const item = items[i]!;
        try {
          if (raw.size > MAX_FILE_MB * 1024 * 1024 && !raw.type.startsWith("image/")) {
            throw new Error(`Die Datei ist zu groß. Erlaubt sind bis zu ${MAX_FILE_MB} MB je Datei.`);
          }
          const prepared = await prepareUpload(raw, false);
          update(item.key, { name: prepared.file.name, status: "upload" });
          const form = new FormData();
          if (prepared.thumb) form.append("thumb", prepared.thumb, "vorschau.jpg");
          form.append("file", prepared.file, prepared.file.name);
          const saved = await upload<FileInfo>(`/api/folders/${folderId}/files`, form, (p) => update(item.key, { progress: p }));
          update(item.key, { status: "done", progress: 1 });
          onSaved(saved, folderId);
        } catch (err) {
          if (onAuth(err)) return;
          update(item.key, { status: "error", error: errorText(err) });
        }
      }
      window.setTimeout(() => setQueue((q) => q.filter((x) => x.status !== "done")), 2500);
    },
    [onSaved, onAuth],
  );

  const dismiss = (key: number) => setQueue((q) => q.filter((x) => x.key !== key));
  return { queue, uploadTo, dismiss };
}

function Queue({ items, onDismiss }: { items: QueueItem[]; onDismiss: (key: number) => void }) {
  if (!items.length) return null;
  return (
    <ul className="queue dqueue" aria-live="polite">
      {items.map((q) => (
        <li key={q.key} className={q.status}>
          <span className="q-name">{q.name}</span>
          <span className="q-state">
            {q.status === "prepare" && "wird vorbereitet"}
            {q.status === "upload" && `${Math.round(q.progress * 100)} %`}
            {q.status === "done" && "gespeichert"}
            {q.status === "error" && q.error}
          </span>
          <span className="q-bar" style={{ transform: `scaleX(${q.progress})` }} />
          {q.status === "error" && (
            <button className="q-x" onClick={() => onDismiss(q.key)} aria-label="Hinweis schließen">
              <Icon name="close" />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Dateien aus dem Explorer (nicht verschobene HandVero-Dateien) im Drag-Ereignis erkennen. */
const hasOsFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");
const hasHvFile = (e: DragEvent) => Array.from(e.dataTransfer.types).includes(HV_FILE);

/* ---------- Ordnerbaum ---------- */

function Tree(props: {
  projectId: string;
  folders: FolderInfo[];
  current?: string;
  onDropFiles: (folderId: string, files: File[]) => void;
  onMoveFile: (fileId: string, folderId: string) => void;
}) {
  const { folders, current } = props;
  const [open, setOpen] = useState<Set<string>>(() => new Set(folderPath(folders, current).map((f) => f.id)));
  const [over, setOver] = useState<string | null>(null);

  // Beim Wechsel in einen tiefen Ordner den Pfad dorthin aufklappen.
  useEffect(() => {
    setOpen((o) => new Set([...o, ...folderPath(folders, current).map((f) => f.id)]));
  }, [current, folders]);

  const toggle = (id: string) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const branch = (parent: string | null, depth: number): ReactNode => {
    const kids = folders.filter((f) => f.parentId === parent);
    if (!kids.length) return null;
    return (
      <ul className="dtree-list" role={depth ? "group" : "tree"}>
        {kids.map((f) => {
          const sub = folders.some((x) => x.parentId === f.id);
          const isOpen = open.has(f.id);
          const { num, label } = splitNumber(f.name);
          const count = deepCount(folders, f.id);
          return (
            <li key={f.id} role="treeitem" aria-expanded={sub ? isOpen : undefined} aria-selected={f.id === current}>
              <div
                className={`dtree-item${f.id === current ? " on" : ""}${over === f.id ? " over" : ""}`}
                style={{ paddingLeft: 10 + depth * 16 }}
                onDragOver={(e) => {
                  if (hasOsFiles(e) || hasHvFile(e)) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = hasHvFile(e) ? "move" : "copy";
                    setOver(f.id);
                  }
                }}
                onDragLeave={() => setOver((o) => (o === f.id ? null : o))}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(null);
                  const moved = e.dataTransfer.getData(HV_FILE);
                  if (moved) props.onMoveFile(moved, f.id);
                  else props.onDropFiles(f.id, Array.from(e.dataTransfer.files));
                }}
              >
                <button className={`dtree-caret${sub ? "" : " none"}`} onClick={() => sub && toggle(f.id)} tabIndex={-1} aria-hidden="true">
                  {sub && <Icon name={isOpen ? "chev" : "right"} />}
                </button>
                <Link to={`/desktop/p/${props.projectId}/f/${f.id}`} className="dtree-link">
                  {num && <span className="dtree-num">{num}</span>}
                  <span className="dtree-label">{label}</span>
                  {count > 0 && <span className="dtree-count">{count}</span>}
                </Link>
              </div>
              {sub && isOpen && branch(f.id, depth + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <nav className="dtree" aria-label="Ordner">
      <Link to={`/desktop/p/${props.projectId}`} className={`dtree-root${current ? "" : " on"}`}>
        <Icon name="grid" /> Alle Ordner
      </Link>
      {branch(null, 0)}
    </nav>
  );
}

/* ---------- Projekt ---------- */

function ProjectPane(props: { me: MeResponse; projectId: string; folderId?: string; protocolId?: string; planId?: string; section: "ordner" | "notizen" | "termine" | "chat" | "protokolle" | "plaene" | "belege" }) {
  const { me, projectId, folderId, section } = props;
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const { settings, update } = useSettings();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [files, setFiles] = useState<FileInfo[] | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sheet, setSheet] = useState<"folder" | "rename" | "menu" | "projectMenu" | "template" | "access" | "handover" | null>(null);
  const [viewer, setViewer] = useState<number | null>(null);
  const [dropping, setDropping] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const isManager = me.user.role !== "employee";

  const loadProject = useCallback(() => {
    api
      .get<ProjectDetail>(`/api/projects/${projectId}`)
      .then(setProject)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [projectId, onAuth]);

  const loadFiles = useCallback(() => {
    if (!folderId) return setFiles([]);
    api
      .get<FileInfo[]>(`/api/folders/${folderId}/files`)
      .then(setFiles)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [folderId, onAuth]);

  useEffect(() => {
    setError("");
    loadProject();
  }, [loadProject]);
  useEffect(() => {
    setFiles(null);
    setViewer(null);
    loadFiles();
  }, [loadFiles]);

  // Fotos vom Handy tauchen ohne Neuladen auf: alle 20 Sekunden nachsehen, solange das Fenster sichtbar ist.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      loadProject();
      loadFiles();
    }, 20_000);
    return () => window.clearInterval(id);
  }, [loadProject, loadFiles]);

  const onSaved = useCallback(
    (file: FileInfo, target: string) => {
      if (target === folderId) setFiles((fs) => [file, ...(fs ?? []).filter((f) => f.id !== file.id)]);
      loadProject();
    },
    [folderId, loadProject],
  );
  const { queue, uploadTo, dismiss } = useUploads(onSaved, onAuth);

  const folders = project?.folders ?? [];
  const path = folderPath(folders, folderId);
  const current = path[path.length - 1];
  const children = folders.filter((f) => f.parentId === (folderId ?? null));
  const base = `/desktop/p/${projectId}`;
  const head = current ? splitNumber(current.name) : null;

  async function moveFile(fileId: string, target: string) {
    if (target === folderId) return;
    try {
      await api.patch(`/api/files/${fileId}`, { folderId: target });
      setFiles((fs) => (fs ?? []).filter((f) => f.id !== fileId));
      const name = folders.find((f) => f.id === target)?.name ?? "";
      setNotice(`Verschoben nach „${splitNumber(name).label}“.`);
      loadProject();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function removeFolder() {
    if (!current) return;
    try {
      await api.del(`/api/folders/${current.id}`);
    } catch (err) {
      if (err instanceof ApiFailure && err.code === "folder_not_empty") {
        if (!window.confirm(`„${current.name}“ enthält Dateien oder Unterordner. Alles darin endgültig löschen?`)) return setSheet(null);
        try {
          await api.del(`/api/folders/${current.id}?mitInhalt=1`);
        } catch (err2) {
          setSheet(null);
          return setError(errorText(err2));
        }
      } else {
        setSheet(null);
        return setError(errorText(err));
      }
    }
    setSheet(null);
    loadProject();
    navigate(current.parentId ? `${base}/f/${current.parentId}` : base, { replace: true });
  }

  async function removeProject() {
    if (!project || !window.confirm(`Projekt „${project.name}“ mit allen Ordnern, Dateien, Notizen und Sprachmemos endgültig löschen?`)) return;
    try {
      await api.del(`/api/projects/${project.id}`);
      navigate("/desktop", { replace: true });
    } catch (err) {
      setSheet(null);
      setError(errorText(err));
    }
  }

  const photos = (files ?? []).filter((f) => f.kind === "photo");
  const docs = (files ?? []).filter((f) => f.kind !== "photo");

  const dropProps = current
    ? {
        onDragOver: (e: DragEvent) => {
          if (hasOsFiles(e)) {
            e.preventDefault();
            setDropping(true);
          }
        },
        onDragLeave: (e: DragEvent) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropping(false);
        },
        onDrop: (e: DragEvent) => {
          if (!hasOsFiles(e)) return;
          e.preventDefault();
          setDropping(false);
          void uploadTo(current.id, Array.from(e.dataTransfer.files));
        },
      }
    : {};

  return (
    <section className="dproject">
      <DHead
        kicker={
          current
            ? [project?.name, ...path.slice(0, -1).map((f) => splitNumber(f.name).label)].filter(Boolean).join(" › ")
            : [project?.customer, project?.address].filter(Boolean).join(" · ") || "Projekt"
        }
        title={head?.label ?? project?.name ?? ""}
        num={head?.num}
        sub={project ? `${project.restricted ? "Nur für ausgewählte Mitarbeiter · " : ""}${project.fileCount} Dateien im Projekt` : undefined}
        actions={
          <button className="dbtn ghost" onClick={() => setSheet("projectMenu")} aria-label="Projekt bearbeiten">
            <Icon name="more" />
          </button>
        }
      >
        <nav className="dsegs" aria-label="Bereiche des Projekts">
          {(
            [
              ["ordner", "Ordner", base],
              ["notizen", "Notizen", `${base}/notizen`],
              ["termine", "Termine", `${base}/termine`],
              ["chat", "Chat", `${base}/chat`],
              ["protokolle", "Protokolle", `${base}/protokolle`],
              ...(me.addons.billing && me.user.role !== "employee" ? ([["belege", "Belege", `${base}/belege`]] as const) : []),
              ...(me.addons.plans ? ([["plaene", "Pläne", `${base}/plaene`]] as const) : []),
            ] as const
          ).map(([id, label, to]) => (
            <Link key={id} to={to} className={section === id ? "on" : undefined} aria-current={section === id ? "page" : undefined}>
              {label}
            </Link>
          ))}
        </nav>
      </DHead>

      <Message>{error}</Message>

      {section === "notizen" && <NotesView projectId={projectId} projectName={project?.name} />}
      {section === "termine" && <CalendarView me={me} projectId={projectId} />}
      {section === "chat" && <ChatView projectId={projectId} />}
      {section === "protokolle" && <ProtocolsView projectId={projectId} protocolId={props.protocolId} base={`${base}/protokolle`} />}
      {section === "belege" && me.addons.billing && me.user.role !== "employee" && <ProjectDocuments projectId={projectId} customerId={project?.customerId ?? null} />}
      {section === "plaene" && me.addons.plans && <PlansView projectId={projectId} planId={props.planId} base={`${base}/plaene`} />}

      {section === "ordner" && (
        <div className="dfolders">
          <aside className="dtree-col">
            {project && (
              <Tree projectId={projectId} folders={folders} current={folderId} onDropFiles={(id, list) => void uploadTo(id, list)} onMoveFile={moveFile} />
            )}
            <button className="dtree-add" onClick={() => setSheet("folder")}>
              <Icon name="plus" /> {current ? "Unterordner" : "Ordner"} anlegen
            </button>
          </aside>

          <div className={`dfiles${dropping ? " dropping" : ""}`} {...dropProps}>
            <div className="dtoolbar">
              <div className="dtoolbar-left">
                {current && (
                  <>
                    <button className="dbtn" onClick={() => fileRef.current?.click()}>
                      <Icon name="plus" /> Dateien hochladen
                    </button>
                    <button className="dbtn ghost" onClick={() => setSheet("menu")}>
                      Ordner bearbeiten
                    </button>
                  </>
                )}
                {!current && <span className="muted-s">Ordner links wählen. Dateien zieht man direkt aus dem Explorer hinein.</span>}
              </div>
              <div className="dsize" role="group" aria-label="Größe der Fotos">
                {([2, 3, 4] as const).map((n) => (
                  <button key={n} className={settings.tiles === n ? "on" : undefined} onClick={() => update({ tiles: n })} aria-pressed={settings.tiles === n}>
                    {n === 2 ? "Groß" : n === 3 ? "Mittel" : "Klein"}
                  </button>
                ))}
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              accept={`image/*,${FILE_ACCEPT}`}
              onChange={(e) => {
                if (current) void uploadTo(current.id, Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />

            <Message kind="ok">{notice}</Message>
            <Queue items={queue} onDismiss={dismiss} />

            {children.length > 0 && (
              <ul className="ftiles dtiles">
                {children.map((f, i) => {
                  const { num, label } = splitNumber(f.name);
                  const total = deepCount(folders, f.id);
                  return (
                    <li key={f.id} style={{ "--i": i } as React.CSSProperties}>
                      <Link
                        to={`${base}/f/${f.id}`}
                        className={`ftile${total ? "" : " empty"}`}
                        onDragOver={(e) => {
                          if (hasOsFiles(e) || hasHvFile(e)) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDropping(false);
                          const moved = e.dataTransfer.getData(HV_FILE);
                          if (moved) void moveFile(moved, f.id);
                          else void uploadTo(f.id, Array.from(e.dataTransfer.files));
                        }}
                      >
                        <span className="ftile-num">{num ?? String(i + 1).padStart(2, "0")}</span>
                        <b>{label}</b>
                        <small>{`${total} ${total === 1 ? "Datei" : "Dateien"}`}</small>
                        <i aria-hidden="true">→</i>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}

            {current && files && files.length === 0 && queue.length === 0 && (
              <div className="ddrop-hint">
                <EmptyState title="Der Ordner ist leer" text="Ziehen Sie Fotos und Dokumente hierher oder fotografieren Sie mit dem Handy. Alles erscheint sofort hier." words={["Rein", "damit"]} />
              </div>
            )}

            {photos.length > 0 && (
              <div className="dgrid" style={{ "--tile": `${TILE_WIDTH[settings.tiles]}px` } as React.CSSProperties}>
                {photos.map((f) => (
                  <button
                    key={f.id}
                    className="dtile"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(HV_FILE, f.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onClick={() => setViewer(files!.indexOf(f))}
                    aria-label={`${f.name} öffnen`}
                  >
                    <img src={`/api/files/${f.id}/${f.hasThumb ? "thumb" : "content"}`} alt="" loading="lazy" draggable={false} />
                    <span className="dtile-cap">
                      <b>{f.name}</b>
                      <small>
                        {new Date(f.createdAt).toLocaleDateString("de-DE")}
                        {f.createdBy && ` · ${f.createdBy}`}
                      </small>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {docs.length > 0 && (
              <ul className="ddocs">
                {docs.map((f) => (
                  <li key={f.id}>
                    <button
                      className="ddoc"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(HV_FILE, f.id);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onClick={() => setViewer(files!.indexOf(f))}
                    >
                      <span className="row-ico doc">{f.name.split(".").pop()?.toUpperCase()}</span>
                      <span className="row-text">
                        <b>{f.name}</b>
                        <small>
                          {sizeText(f.size)} · {new Date(f.createdAt).toLocaleDateString("de-DE")}
                          {f.createdBy && ` · ${f.createdBy}`}
                        </small>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {dropping && current && (
              <div className="ddrop" aria-hidden="true">
                <b>Loslassen</b>
                <span>und alles landet in „{splitNumber(current.name).label}“</span>
              </div>
            )}
          </div>
        </div>
      )}

      {sheet === "folder" && (
        <FolderNameSheet
          title={current ? `Neuer Ordner in „${splitNumber(current.name).label}“` : "Neuer Ordner"}
          submitLabel="Ordner anlegen"
          onClose={() => setSheet(null)}
          onSubmit={async (name) => {
            const f = await api.post<FolderInfo>(`/api/projects/${projectId}/folders`, { name, parentId: folderId ?? null });
            setSheet(null);
            loadProject();
            navigate(`${base}/f/${f.id}`);
          }}
        />
      )}
      {sheet === "rename" && current && (
        <FolderNameSheet
          title="Ordner umbenennen"
          initial={current.name}
          submitLabel="Speichern"
          onClose={() => setSheet(null)}
          onSubmit={async (name) => {
            setProject(await api.patch<ProjectDetail>(`/api/folders/${current.id}`, { name }));
            setSheet(null);
          }}
        />
      )}
      {sheet === "template" && project && (
        <FolderNameSheet
          title="Als Vorlage speichern"
          initial={`Vorlage ${project.name}`}
          submitLabel="Vorlage speichern"
          onClose={() => setSheet(null)}
          onSubmit={async (name) => {
            await api.post("/api/templates", { name, fromProjectId: project.id });
            setSheet(null);
            setNotice(`Die Ordnerstruktur ist jetzt als Vorlage „${name}“ für neue Projekte verfügbar.`);
          }}
        />
      )}
      {sheet === "menu" && current && (
        <Sheet title={current.name} onClose={() => setSheet(null)}>
          <div className="menu">
            <button onClick={() => setSheet("folder")}>Unterordner anlegen</button>
            <button onClick={() => setSheet("rename")}>Umbenennen</button>
            <button className="danger" onClick={() => void removeFolder()}>
              Ordner löschen
            </button>
          </div>
        </Sheet>
      )}
      {sheet === "projectMenu" && project && (
        <Sheet title={project.name} onClose={() => setSheet(null)}>
          <div className="menu">
            {isManager && (
              <button onClick={() => setSheet("access")}>
                Wer sieht das Projekt? <small>{project.restricted ? "Ausgewählte Mitarbeiter" : "Alle in der Firma"}</small>
              </button>
            )}
            <button onClick={() => setSheet("handover")}>Übergabe-Akte an den Kunden schicken</button>
            {isManager && <button onClick={() => setSheet("template")}>Ordnerstruktur als Vorlage speichern</button>}
            {isManager && (
              <button className="danger" onClick={() => void removeProject()}>
                Projekt löschen
              </button>
            )}
            {!isManager && <p className="muted-s">Freigaben ändern, Vorlagen speichern und Projekte löschen können Inhaber und Admins.</p>}
          </div>
        </Sheet>
      )}
      {sheet === "handover" && project && <HandoverSheet project={project} onClose={() => setSheet(null)} />}
      {sheet === "access" && project && <ProjectAccessSheet project={project} onClose={() => setSheet(null)} onSaved={setProject} />}
      {viewer !== null && files?.[viewer] && (
        <Viewer
          files={files}
          index={viewer}
          onIndex={setViewer}
          onClose={() => setViewer(null)}
          onDeleted={(id) => {
            setFiles((fs) => (fs ?? []).filter((f) => f.id !== id));
            setViewer(null);
            loadProject();
          }}
        />
      )}
    </section>
  );
}

/* ---------- Projektliste ---------- */

/** Erste Schritte: kurze Liste für neue Firmen. Haken setzen sich aus den vorhandenen Daten, der Rest merkt sich der Browser. */
function FirstSteps({ me, projects, onNewProject }: { me: MeResponse; projects: ProjectSummary[]; onNewProject: () => void }) {
  const key = `hv-start-${me.company.id}`;
  const read = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem(key) ?? "[]") as string[];
    } catch {
      return [];
    }
  };
  const [flags, setFlags] = useState<string[]>(read);
  const mark = (id: string) =>
    setFlags((f) => {
      const next = f.includes(id) ? f : [...f, id];
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* ohne Speicher bleibt es bei dieser Ansicht */
      }
      return next;
    });
  const manager = me.user.role !== "employee";
  const steps: { id: string; title: string; text: string; done: boolean; action: ReactNode }[] = [
    {
      id: "projekt",
      title: "Erstes Projekt anlegen",
      text: "Ein Projekt je Auftrag oder Kunde. Die Ordner kommen aus einer Vorlage, zum Beispiel „Elektroinstallation“.",
      done: projects.length > 0,
      action: (
        <button className="dbtn small" onClick={onNewProject}>
          Projekt anlegen
        </button>
      ),
    },
    {
      id: "foto",
      title: "Erstes Foto oder Dokument ablegen",
      text: "Ziehen Sie eine Datei in einen Ordner oder fotografieren Sie mit dem Handy direkt hinein.",
      done: projects.some((p) => p.fileCount > 0),
      action: null,
    },
    {
      id: "handy",
      title: "Handy verbinden",
      text: "Dann landen Fotos von der Baustelle sofort im richtigen Ordner.",
      done: flags.includes("handy"),
      action: (
        <Link className="dbtn ghost small" to="/desktop/handy" onClick={() => mark("handy")}>
          Anleitung öffnen
        </Link>
      ),
    },
    ...(manager
      ? [
          {
            id: "team",
            title: "Kollegen einladen",
            text: "Jeder bekommt einen eigenen Zugang. Was er sieht, legen Sie fest.",
            done: me.members.length > 1 || me.invitations.length > 0,
            action: (
              <Link className="dbtn ghost small" to="/kundenbereich">
                Einladen
              </Link>
            ),
          },
        ]
      : []),
    {
      id: "vero",
      title: "Vero fragen",
      text: "Das Maskottchen unten rechts erklärt jede Funktion und schlägt vor, wohin eine Notiz gehört. Tippen Sie es an und stellen Sie eine Frage.",
      done: flags.includes("vero"),
      action: (
        <button className="dbtn ghost small" onClick={() => mark("vero")}>
          Habe ich ausprobiert
        </button>
      ),
    },
  ];
  const left = steps.filter((x) => !x.done).length;
  if (flags.includes("aus") || left === 0) return null;
  return (
    <section className="dfirst" aria-label="Erste Schritte">
      <header>
        <h3>Erste Schritte</h3>
        <small>
          {steps.length - left} von {steps.length} erledigt
        </small>
        <button className="dbtn ghost small" onClick={() => mark("aus")}>
          Ausblenden
        </button>
      </header>
      <ol>
        {steps.map((x) => (
          <li key={x.id} className={x.done ? "done" : undefined}>
            <span className="dfirst-check" aria-hidden="true">
              {x.done ? "✓" : ""}
            </span>
            <span className="dfirst-text">
              <b>{x.title}</b>
              {!x.done && <small>{x.text}</small>}
            </span>
            {!x.done && x.action}
          </li>
        ))}
      </ol>
    </section>
  );
}

function ProjectsPage({ me }: { me: MeResponse }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const onAuth = useUnauthorizedRedirect();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const projectId = params.projectId;
  const section = location.pathname.endsWith("/notizen")
    ? "notizen"
    : location.pathname.endsWith("/termine")
      ? "termine"
      : location.pathname.endsWith("/chat")
        ? "chat"
      : location.pathname.endsWith("/belege")
        ? "belege"
      : location.pathname.includes("/protokolle")
        ? "protokolle"
        : location.pathname.includes("/plaene")
          ? "plaene"
          : "ordner";

  const load = useCallback(() => {
    api
      .get<ProjectSummary[]>("/api/projects")
      .then(setProjects)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [onAuth]);
  // Liste aktuell halten (Dateizähler, neue Projekte vom Handy).
  useEffect(() => {
    load();
    const id = window.setInterval(() => document.visibilityState === "visible" && load(), 30_000);
    return () => window.clearInterval(id);
  }, [load, projectId]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!projects) return [];
    return q ? projects.filter((p) => `${p.name} ${p.customer ?? ""} ${p.address ?? ""}`.toLowerCase().includes(q)) : projects;
  }, [projects, query]);

  return (
    <div className={`dprojects-layout${projectId ? " has-project" : ""}`}>
      <section className="dplist">
        <div className="dplist-head">
          <p className="dkicker">▼ {me.company.name}</p>
          <h2>Projekte</h2>
          <div className="dsearch">
            <Icon name="search" />
            <input type="search" placeholder="Suchen" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Projekte durchsuchen" />
          </div>
        </div>
        <Message>{error}</Message>
        <GlobalHits base="/desktop" query={query} />
        <ol className="dpl">
          {shown.map((p, i) => (
            <li key={p.id}>
              <NavLink to={`/desktop/p/${p.id}`} className={() => `dpl-item${p.id === projectId ? " on" : ""}`}>
                <span className="pnum">{String(i + 1).padStart(2, "0")}</span>
                <span className="ptext">
                  <b>{p.name}</b>
                  <small>{[p.customer, p.address].filter(Boolean).join(" · ") || "Ohne Kundenangabe"}</small>
                </span>
                {p.restricted && (
                  <span className="plock" title="Nur für ausgewählte Mitarbeiter">
                    <Icon name="lock" />
                  </span>
                )}
                <span className="dpl-count">{p.fileCount}</span>
              </NavLink>
            </li>
          ))}
        </ol>
        {projects && projects.length === 0 && <p className="muted-s dpl-empty">Noch keine Projekte. Legen Sie unten das erste an.</p>}
        <button className="dbtn wide" onClick={() => setCreating(true)}>
          <Icon name="plus" /> Neues Projekt
        </button>
      </section>

      {projectId ? (
        <ProjectPane key={projectId} me={me} projectId={projectId} folderId={params.folderId} protocolId={params.protocolId} planId={params.planId} section={section} />
      ) : (
        <section className="dproject dwelcome">
          {projects && <FirstSteps me={me} projects={projects} onNewProject={() => setCreating(true)} />}
          <TodayPanel base="/desktop" />
          <EmptyState
            title={projects?.length ? "Projekt wählen" : "Willkommen bei HandVero"}
            text={
              projects?.length
                ? "Links ein Projekt anklicken. Ordner, Fotos, Notizen und Termine erscheinen hier, auch alles, was gerade auf dem Handy entsteht."
                : "Legen Sie Ihr erstes Projekt an. Die Ordner kommen auf Wunsch gleich aus einer Vorlage."
            }
            words={["Foto", "Ordner", "Fertig"]}
          />
        </section>
      )}

      {creating && (
        <NewProject
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setCreating(false);
            load();
            navigate(`/desktop/p/${p.id}`);
          }}
        />
      )}
    </div>
  );
}

/* ---------- Rahmen ---------- */

interface NavLeaf {
  label: string;
  to: string;
  /** Passt die aktuelle Adresse zu diesem Punkt? */
  match: (path: string) => boolean;
  /** Symbol für die schmale Leiste. */
  icon: IconName;
}
interface NavGroup {
  id: string;
  label: string;
  children: NavLeaf[];
}

const OTHER_AREAS = /^\/desktop\/(notizen|kalender|kunden|firma|belege|schulungen|handy|admin|konto|geraete)/;

/** Die großen Bereiche der Seitenleiste; darunter klappen die einzelnen Seiten auf. */
function navGroups(me: MeResponse): NavGroup[] {
  const at = (prefix: string) => (path: string) => path === prefix || path.startsWith(`${prefix}/`);
  const groups: NavGroup[] = [
    {
      id: "geschaeft",
      label: "Baustelle & Büro",
      children: [
        { label: "Projekte", icon: "folder", to: "/desktop", match: (p: string) => !OTHER_AREAS.test(p) },
        { label: "Geräte", icon: "clipboard", to: "/desktop/geraete", match: at("/desktop/geraete") },
        ...(me.addons.billing
          ? ([
              { label: "Kunden", icon: "user", to: "/desktop/kunden", match: at("/desktop/kunden") },
              { label: "Angebote", icon: "file", to: "/desktop/belege/angebote", match: at("/desktop/belege/angebote") },
              { label: "Aufträge", icon: "clipboard", to: "/desktop/belege/auftraege", match: at("/desktop/belege/auftraege") },
              { label: "Rechnungen", icon: "euro", to: "/desktop/belege/rechnungen", match: (p: string) => at("/desktop/belege")(p) && !/\/belege\/(angebote|auftraege|nummern|einstellungen|mahnungen|ausgaben|bank)/.test(p) },
              { label: "Mahnungen", icon: "bell", to: "/desktop/belege/mahnungen", match: at("/desktop/belege/mahnungen") },
              { label: "Ausgaben", icon: "download", to: "/desktop/belege/ausgaben", match: at("/desktop/belege/ausgaben") },
              { label: "Bank", icon: "bank", to: "/desktop/belege/bank", match: at("/desktop/belege/bank") },
              ...(me.user.role !== "employee" ? ([{ label: "Firmenangaben", icon: "settings", to: "/desktop/firma", match: at("/desktop/firma") }] as NavLeaf[]) : []),
              { label: "Nummernkreise", icon: "settings", to: "/desktop/belege/einstellungen", match: (p: string) => /\/belege\/(einstellungen|nummern)/.test(p) },
            ] as NavLeaf[])
          : []),
      ],
    },
    {
      id: "team",
      label: "Team",
      children: [
        { label: "Schulungen", icon: "cap", to: "/desktop/schulungen", match: at("/desktop/schulungen") },
        { label: "Kalender", icon: "calendar", to: "/desktop/kalender", match: at("/desktop/kalender") },
        { label: "Notizen", icon: "note", to: "/desktop/notizen", match: at("/desktop/notizen") },
      ],
    },
    {
      id: "verbunden",
      label: "Handy",
      children: [{ label: "Handy koppeln", icon: "phone", to: "/desktop/handy", match: at("/desktop/handy") }],
    },
  ];
  if (me.superAdmin) groups.push({ id: "admin", label: "Admin", children: [{ label: "Verwaltung", icon: "shield", to: "/desktop/admin", match: at("/desktop/admin") }] });
  return groups;
}

const RAIL_KEY = "hv.desk.rail";

function SideNav({ me }: { me: MeResponse }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Eingeklappt: nur eine schmale Leiste mit Symbolen, damit mehr Platz zum Arbeiten bleibt. Gilt pro Gerät.
  const [rail, setRail] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleRail = () =>
    setRail((r) => {
      try {
        localStorage.setItem(RAIL_KEY, r ? "0" : "1");
      } catch {
        /* ohne Speicher gilt die Wahl nur bis zum Neuladen */
      }
      return !r;
    });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const area = /^\/desktop\/(konto)/.exec(pathname)?.[1] ?? "";
  const groups = navGroups(me);
  const activeGroup = groups.find((g) => g.children.some((c) => c.match(pathname)))?.id;
  const [open, setOpen] = useState<Set<string>>(() => new Set(activeGroup ? [activeGroup] : []));
  // Beim Seitenwechsel öffnet sich der passende Bereich von selbst; von Hand geöffnete bleiben offen.
  useEffect(() => {
    if (activeGroup) setOpen((o) => (o.has(activeGroup) ? o : new Set(o).add(activeGroup)));
  }, [activeGroup]);
  const toggle = (id: string) =>
    setOpen((o) => {
      const n = new Set(o);
      if (!n.delete(id)) n.add(id);
      return n;
    });
  async function logout() {
    await api.post("/api/auth/logout").catch(() => undefined);
    navigate("/desktop/anmelden", { replace: true });
  }
  return (
    <aside className={`dside${rail ? " rail" : ""}`}>
      <button type="button" className="dside-fold" onClick={toggleRail} aria-label={rail ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"} title={rail ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}>
        <Icon name="panel" />
      </button>
      <Link to="/desktop" className="dside-logo" title="HandVero">
        {LOGO}
        <span>
          <b>HandVero</b>
          <small>{me.liveUrl ? "Vorschau" : me.company.name}</small>
        </span>
      </Link>
      {rail && (
        <nav className="dside-rail" aria-label="Hauptbereiche">
          {groups.map((g) => (
            <div key={g.id} className="dside-rail-group">
              {g.children.map((c) => {
                const on = c.match(pathname);
                return (
                  <Link key={c.to} to={c.to} className={on ? "on" : undefined} aria-current={on ? "page" : undefined} aria-label={c.label} title={c.label}>
                    <Icon name={c.icon} />
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
      )}
      <nav className="dside-nav" aria-label="Hauptbereiche" hidden={rail}>
        {groups.map((g, i) => {
          const num = String(i + 1).padStart(2, "0");
          const isActive = g.id === activeGroup;
          if (g.children.length === 1) {
            const c = g.children[0]!;
            return (
              <Link key={g.id} to={c.to} className={`dnav-single${isActive ? " on" : ""}`} aria-current={isActive ? "page" : undefined}>
                <span>{num}</span>
                {g.label}
              </Link>
            );
          }
          const expanded = open.has(g.id);
          return (
            <div key={g.id} className={`dnav-group${expanded ? " open" : ""}${isActive ? " active" : ""}`}>
              <button type="button" className="dnav-head" onClick={() => toggle(g.id)} aria-expanded={expanded} aria-controls={`nav-${g.id}`}>
                <span>{num}</span>
                {g.label}
                <i aria-hidden="true" />
              </button>
              <div className="dnav-body" id={`nav-${g.id}`}>
                <ul>
                  {g.children.map((c) => {
                    const on = c.match(pathname);
                    return (
                      <li key={c.to}>
                        <Link to={c.to} className={on ? "on" : undefined} aria-current={on ? "page" : undefined} tabIndex={expanded ? 0 : -1}>
                          {c.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </nav>
      <div className="dside-foot">
        <span className="dside-user">
          <Icon name="user" />
          <span>
            <b>{me.user.name}</b>
            <small>{me.company.planLabel}</small>
          </span>
        </span>
        {me.superAdmin && <PrivatSwitch />}
        {me.previewUrl && (
          <a className="dside-btn dside-preview" href={`${me.previewUrl}/desktop`} title="Neue Funktionen testen, bevor sie freigegeben werden">
            <Icon name="eye" /> Vorschau testen
          </a>
        )}
        {me.liveUrl && (
          <a className="dside-btn dside-preview" href={`${me.liveUrl}/desktop`} title="Zurück zur Version, die alle Kunden nutzen">
            <Icon name="back" /> Zur freigegebenen Version
          </a>
        )}
        <Link className={`dside-btn${area === "konto" ? " on" : ""}`} to="/desktop/konto" title="Mein Konto">
          <Icon name="lock" /> Mein Konto
        </Link>
        <button className="dside-btn" onClick={() => setSettingsOpen(true)} title="Einstellungen">
          <Icon name="settings" /> Einstellungen
          <PcSyncBadge />
        </button>
        <Link className="dside-btn" to="/kundenbereich" title="Kundenbereich">
          <Icon name="user" /> Kundenbereich
        </Link>
        <button className="dside-btn" onClick={() => void logout()} title="Abmelden">
          <Icon name="back" /> Abmelden
        </button>
      </div>
      {settingsOpen && <SettingsSheet onClose={() => setSettingsOpen(false)} />}
    </aside>
  );
}

const BELEGE_TITLE: Record<string, string> = { quote: "Angebote", order: "Aufträge", invoice: "Rechnungen", numbers: "Nummernkreise", dunning: "Mahnungen" };

function BelegePage({ me }: { me: MeResponse }) {
  const param = useParams().tab ?? "";
  if (param === "uebersicht" || param === "ausgaben" || param === "bank") {
    return (
      <SimplePage kicker={me.company.name} title={param === "ausgaben" ? "Ausgaben und Belege" : param === "bank" ? "Bank" : "Zahlen und Export"}>
        {param === "ausgaben" ? <ExpensesView me={me} /> : param === "bank" ? <BankView me={me} /> : <DashboardView me={me} />}
      </SimplePage>
    );
  }
  const tab = DOC_TABS[param] ?? "invoice";
  return (
    <SimplePage kicker={me.company.name} title={BELEGE_TITLE[tab]!}>
      {tab === "invoice" && (
        <p className="muted-s">
          <Link to="/desktop/belege/uebersicht">Zahlen und Export für den Steuerberater ansehen</Link>
        </p>
      )}
      <DocumentsView me={me} tab={tab} key={tab} />
    </SimplePage>
  );
}

function SimplePage({ kicker, title, children }: { kicker: string; title: string; children: ReactNode }) {
  return (
    <section className="dproject dsimple">
      <DHead kicker={kicker} title={title} />
      {children}
    </section>
  );
}

export function DesktopApp() {
  const onAuth = useUnauthorizedRedirect();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.classList.add("in-app", "in-desk");
    document.title = "HandVero";
    return () => document.documentElement.classList.remove("in-app", "in-desk");
  }, []);

  // Nur beim Start und nach Änderungen am Konto laden, nicht bei jedem Seitenwechsel.
  const onAuthRef = useRef(onAuth);
  onAuthRef.current = onAuth;
  const reloadMe = useCallback(() => {
    api
      .get<MeResponse>("/api/me")
      .then((m) => {
        setMe(m);
        setError("");
      })
      .catch((err) => onAuthRef.current(err) || setError(errorText(err)));
  }, []);
  useEffect(reloadMe, [reloadMe]);
  // In der Windows-App: Abgleich mit dem gewählten Ordner, solange man angemeldet ist.
  const signedIn = me !== null;
  useEffect(() => (signedIn ? startPcSync() : undefined), [signedIn]);

  return (
    <SettingsProvider>
      <div className="hv-app hv-desk">
        {!me && !error && <div className="boot">{LOGO}</div>}
        {error && !me && (
          <main className="app-main dboot-error">
            <Message>{error}</Message>
          </main>
        )}
        {me && (
          <>
            <SideNav me={me} />
            <main className="dmain">
              <Routes>
                <Route index element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/notizen" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/termine" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/chat" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/belege" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/protokolle" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/protokolle/:protocolId" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/plaene" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/plaene/:planId" element={<ProjectsPage me={me} />} />
                <Route path="p/:projectId/f/:folderId" element={<ProjectsPage me={me} />} />
                <Route
                  path="notizen"
                  element={
                    <SimplePage kicker={me.company.name} title="Notizen">
                      <NotesView />
                    </SimplePage>
                  }
                />
                <Route
                  path="kalender"
                  element={
                    <SimplePage kicker={me.company.name} title="Kalender">
                      <CalendarView me={me} />
                    </SimplePage>
                  }
                />
                <Route path="schulungen/*" element={<TrainingsPage me={me} />} />
                <Route
                  path="handy"
                  element={
                    <SimplePage kicker="Fotos direkt in den Ordner" title="Handy verknüpfen">
                      <div className="dphone">
                        <PhoneLinkPanel />
                        <div className="dphone-text">
                          <h2>Ein Scan, und das Handy gehört dazu.</h2>
                          <p>
                            Auf dem Handy öffnet sich HandVero bereits angemeldet. Jedes Foto, jedes Sprachmemo und jede Notiz landet sofort hier am
                            PC, im richtigen Projekt und Ordner.
                          </p>
                          <p className="muted-s">Jeder Code gilt fünf Minuten und nur für ein Handy. Für weitere Handys einfach einen neuen anzeigen.</p>
                        </div>
                      </div>
                    </SimplePage>
                  }
                />
                {me.addons.billing && <Route path="belege" element={<BelegePage me={me} />} />}
                {me.addons.billing && <Route path="belege/:tab" element={<BelegePage me={me} />} />}
                {me.addons.billing && me.user.role !== "employee" && (
                  <Route
                    path="firma"
                    element={
                      <SimplePage kicker={me.company.name} title="Firmenangaben">
                        <BillingForm />
                      </SimplePage>
                    }
                  />
                )}
                {me.addons.billing && (
                  <Route
                    path="kunden"
                    element={
                      <SimplePage kicker={me.company.name} title="Kunden">
                        <CustomersView me={me} />
                      </SimplePage>
                    }
                  />
                )}
                <Route
                  path="geraete"
                  element={
                    <SimplePage kicker={me.company.name} title="Geräte">
                      <DevicesView base="/desktop" />
                    </SimplePage>
                  }
                />
                <Route
                  path="konto"
                  element={
                    <SimplePage kicker={me.company.name} title="Mein Konto">
                      <AccountView me={me} onChanged={reloadMe} />
                    </SimplePage>
                  }
                />
                {me.superAdmin && <Route path="admin/*" element={<AdminPage />} />}
                <Route path="*" element={<ProjectsPage me={me} />} />
              </Routes>
            </main>
            <QueueBanner />
            <SupportBanner support={me.support} />
            <Mascot variant="desk" />
            <GuideHost me={me} variant="desk" />
          </>
        )}
      </div>
    </SettingsProvider>
  );
}


/** Nur für den Super-Admin: App in der hellblauen Privat-Optik ansehen, um sie zu prüfen. Ändert keine Daten. */
function PrivatSwitch() {
  const read = () => {
    try {
      return localStorage.getItem("hv_ansicht") === "privat";
    } catch {
      return false;
    }
  };
  const [privat, setPrivat] = useState(read);
  useEffect(() => {
    if (privat) document.documentElement.dataset.mode = "privat";
    else delete document.documentElement.dataset.mode;
    return () => {
      delete document.documentElement.dataset.mode;
    };
  }, [privat]);
  function toggle() {
    const next = !privat;
    setPrivat(next);
    try {
      localStorage.setItem("hv_ansicht", next ? "privat" : "betrieb");
    } catch {
      /* ohne Speicher gilt die Wahl nur für diese Sitzung */
    }
  }
  return (
    <>
      <button className="dside-btn" onClick={toggle} title="Zwischen Betriebs- und Privat-Optik wechseln (nur zum Prüfen)">
        <Icon name="eye" /> Ansicht: {privat ? "Privat" : "Betrieb"}
      </button>
      {privat && (
        <Link className="dside-btn" to="/privat" title="Die Privat-Seiten der Website ansehen">
          <Icon name="eye" /> Privat-Website
        </Link>
      )}
    </>
  );
}
